import {CurrentUserService} from "../../services/user/CurrentUserService";
import {SystemEventService} from "../../services/system-state/SystemEventService";
import {Observable, ReplaySubject, Scheduler, Subject} from "rxjs";
import {IconMapping, iconMapping} from "../data/IconMappings";
import {createNotificationFavicon, findExistingIcon} from "./FaviconUtil";
import {SystemEvent} from "../../services/system-state/SystemEvent";
import {BrowserThemeStore} from "../../services/browser-theme/BrowserThemeStore";
import {BrowserTheme} from "../../services/browser-theme/BrowserTheme";

export class FaviconService {

    public static id = "faviconService";

    private readonly headElement$ = new ReplaySubject<HTMLHeadElement>(1);
    private readonly faviconSource$ = new ReplaySubject<IconMapping>(1);
    private readonly defaultFavicon$ = new ReplaySubject<HTMLLinkElement>(1);
    private readonly notificationFavicon$ = new ReplaySubject<HTMLLinkElement>(1);

    private readonly notificationShown$ = new Subject<boolean>();

    /** @ngInject */
    public constructor(private readonly currentUserService: CurrentUserService,
                       private readonly systemEventService: SystemEventService,
                       private readonly browserThemeStore: BrowserThemeStore) {
        this.init();
    }

    private init() {
        this.initiateHead();
        this.watchUser();
        this.createDefaultIcon();
        this.createNotificationIcon();
        this.watchFocus();
    }

    private initiateHead() {
        const head = document.head;
        if (head == null) {
            throw Error("Did not get head");
        } else {
            this.headElement$.next(head);
        }
    }

    private watchUser() {
        this.currentUserService
            .current
            .map((u) => u.branding())
            .map((branding) => iconMapping(branding))
            .subscribe((source) => this.faviconSource$.next(source));
    }

    private createDefaultIcon() {
        const source$ = Observable.combineLatest(this.faviconSource$, this.browserThemeStore.current);
        source$.observeOn(Scheduler.async)
               .map(([mapping, theme]) => this.themeIcon(mapping, theme))
               .map((source) => this.createFaviconElement(source))
               .subscribe((element) => this.defaultFavicon$.next(element));
    }

    private themeIcon(mapping: IconMapping, theme: BrowserTheme): string {
        if (theme === BrowserTheme.LIGHT) {
            return mapping.lightIcon;
        } else {
            return mapping.darkIcon;
        }
    }

    private createNotificationIcon() {
        const source$ = Observable.combineLatest(this.faviconSource$, this.browserThemeStore.current);

        source$.observeOn(Scheduler.async)
               .flatMap(([mapping, theme]) => createNotificationFavicon(mapping, theme))
               .map((source) => this.createFaviconElement(source))
               .subscribe((element) => this.notificationFavicon$.next(element));
    }

    private createFaviconElement(source: string): HTMLLinkElement {
        const link = document.createElement("link");
        link.type = "image/x-icon";
        link.rel = "icon";
        link.href = source;
        return link;
    }

    private removeExistingFavicon() {
        findExistingIcon().forEach((tag) => tag.remove());
    }

    private watchFocus() {
        const notificationShown$ = this.notificationShown$
                                       .switchMap((_) => this.notificationFavicon$);

        const focus$ = this.systemEventService.eventsOfType(SystemEvent.WINDOW_FOCUS);
        const blur$ = this.systemEventService.eventsOfType(SystemEvent.WINDOW_BLUR);

        Observable.merge(focus$, blur$)
                  .switchMap((event) => {
                      if (event === SystemEvent.WINDOW_FOCUS) {
                          return this.defaultFavicon$;
                      } else {
                          return notificationShown$;
                      }
                  })
                  .distinctUntilChanged()
                  .withLatestFrom(this.headElement$)
                  .do(() => this.removeExistingFavicon())
                  .subscribe(([favicon, head]) => head.appendChild(favicon));
    }

    public notificationShown() {
        this.notificationShown$.next(true);
    }
}
